import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { ArrowLeft, Send, AlertTriangle, Info, AlertCircle, Clock } from 'lucide-react';
import axios from 'axios';

interface Category {
  id: number;
  name: string;
  description: string | null;
  defaultPriority: string;
}

interface Classroom {
  id: number;
  name: string;
  building: string | null;
}

const priorityLabels: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  P0: { label: 'Crítico - Atención inmediata', color: 'bg-red-100 text-red-800 border-red-300', icon: <AlertTriangle className="w-4 h-4" /> },
  P1: { label: 'Alto - Urgente', color: 'bg-orange-100 text-orange-800 border-orange-300', icon: <AlertCircle className="w-4 h-4" /> },
  P2: { label: 'Medio - Normal', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: <Clock className="w-4 h-4" /> },
  P3: { label: 'Bajo - Puede esperar', color: 'bg-green-100 text-green-800 border-green-300', icon: <Info className="w-4 h-4" /> },
};

export default function NuevoTicket() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    categoryId: '',
    priority: 'P2',
    location: '',
    classroomId: '',
  });

  useEffect(() => {
    fetchCategories();
    fetchClassrooms();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/tickets/categories');
      setCategories(response.data.filter((c: Category) => c));
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const response = await axios.get('/api/classrooms');
      setClassrooms(response.data || []);
    } catch (err) {
      console.error('Error fetching classrooms:', err);
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setForm({ ...form, categoryId });
    const category = categories.find(c => c.id === parseInt(categoryId, 10));
    if (category) {
      setForm(prev => ({ ...prev, categoryId, priority: category.defaultPriority }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await axios.post('/api/tickets', {
        title: form.title,
        description: form.description,
        categoryId: form.categoryId ? parseInt(form.categoryId, 10) : null,
        priority: form.priority,
        location: form.location || null,
        classroomId: form.classroomId ? parseInt(form.classroomId, 10) : null,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/app/tickets/mis-tickets');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al crear el ticket');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Send className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-semibold text-green-800 mb-2">Ticket Enviado</h2>
          <p className="text-green-600">Tu solicitud ha sido registrada. Te notificaremos cuando haya actualizaciones.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Reportar Incidencia</h1>
            <p className="text-gray-500">Describe el problema para que el equipo de mantenimiento pueda atenderlo</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">
              Categoría del Problema
            </label>
            <select
              value={form.categoryId}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">Seleccionar categoría...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Título del Reporte *
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ej: Horno no enciende, Fuga de agua en lavabo..."
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Descripción Detallada *
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Describe el problema con el mayor detalle posible: qué ocurrió, cuándo empezó, qué intentaste..."
              className="w-full border rounded-lg px-3 py-2 min-h-[120px]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Prioridad
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(priorityLabels).map(([key, { label, color, icon }]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setForm({ ...form, priority: key })}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-left transition-all ${
                    form.priority === key ? `${color} border-2` : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {icon}
                  <div>
                    <div className="font-medium text-sm">{key}</div>
                    <div className="text-xs opacity-75">{label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Aula / Salón
            </label>
            <select
              value={form.classroomId}
              onChange={(e) => setForm({ ...form, classroomId: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">Seleccionar aula...</option>
              {classrooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name} {room.building ? `(${room.building})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Ubicación Adicional
            </label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Ej: Primer piso, junto a la entrada principal..."
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar Reporte'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
